import React, { useState, useEffect } from "react";

const CurrencyConverter = () => {
  const [amount, setAmount] = useState(1000);
  const [exchangeRate, setExchangeRate] = useState(83.50);
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("INR");
  const [conversionType, setConversionType] = useState("standard");
  const [roundUp, setRoundUp] = useState(false);
  const [feePercentage, setFeePercentage] = useState(0);
  const [result, setResult] = useState({
    originalAmount: "0.00",
    exchangeRate: "0.00",
    convertedAmount: "0.00",
    conversionFee: "0.00",
    totalAmount: "0.00",
    perUnitRate: "0.00",
    reverseRate: "0.00",
    conversionTypeText: "",
    currencyTip: "",
  });

  // Currency Conversion Function
  const convertCurrency = () => {
    let amt = parseFloat(amount);
    let rate = parseFloat(exchangeRate);
    let fee = parseFloat(feePercentage);

    if (isNaN(amt) || amt < 0 || isNaN(rate) || rate <= 0 || isNaN(fee) || fee < 0) return;

    // Calculate converted amount
    let convertedAmount = amt * rate;
    
    // Calculate conversion fee
    let conversionFee = (convertedAmount * fee) / 100;
    
    // Round up if selected
    if (roundUp) {
      convertedAmount = Math.ceil(convertedAmount);
      conversionFee = Math.ceil(conversionFee);
    }
    
    // Calculate total amount after fees
    const totalAmount = convertedAmount + conversionFee;
    
    // Calculate per unit rate
    const perUnitRate = rate;
    
    // Calculate reverse rate
    const reverseRate = 1 / rate;

    let typeText = "";
    let currencyTip = "";

    switch(conversionType) {
      case "standard":
        typeText = "Standard Conversion";
        currencyTip = "Standard conversion uses mid-market rates. Actual rates from banks/exchangers may vary slightly.";
        break;
      case "travel":
        typeText = "Travel Exchange";
        currencyTip = "Travel exchange includes markup. Airport exchanges charge 3-5% higher rates. Book forex cards for better rates.";
        break;
      case "business":
        typeText = "Business Transaction";
        currencyTip = "Business conversions may have lower fees for bulk amounts. Consider forward contracts to lock rates.";
        break;
      case "remittance":
        typeText = "Money Transfer/Remittance";
        currencyTip = "Remittance services charge transfer fees plus currency markup. Compare services like Wise, Western Union, and banks.";
        break;
      case "custom":
        typeText = "Custom Rate Conversion";
        currencyTip = "Custom rates useful for calculations. Always verify current rates before actual transactions.";
        break;
      default:
        typeText = "Currency Conversion";
        currencyTip = "Exchange rates fluctuate continuously based on market conditions and economic factors.";
    }

    setResult({
      originalAmount: amt.toFixed(2),
      exchangeRate: rate.toFixed(4),
      convertedAmount: convertedAmount.toFixed(2),
      conversionFee: conversionFee.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      perUnitRate: perUnitRate.toFixed(4),
      reverseRate: reverseRate.toFixed(6),
      conversionTypeText: typeText,
      currencyTip,
    });
  };

  useEffect(() => {
    convertCurrency();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <h1>Currency Converter</h1>
          <p>
            Convert between currencies with live exchange rates, fees calculation, and detailed breakup
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Conversion Details
              </h2>

              {/* Amount */}
              <div className="input-group">
                <label>Amount ({fromCurrency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="calc-input"
                    placeholder="1000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100, 500, 1000, 5000].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setAmount(amt)}
                      className="quick-select-btn primary"
                    >
                      {amt >= 1000 ? `${(amt / 1000).toFixed(0)}K` : amt}
                    </button>
                  ))}
                </div>
              </div>

              {/* From Currency */}
              <div className="input-group">
                <label>From Currency</label>
                <div className="quick-select-group">
                  {[
                    { value: "USD", label: "USD ($)" },
                    { value: "EUR", label: "EUR (€)" },
                    { value: "GBP", label: "GBP (£)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setFromCurrency(type.value);
                        if (type.value === "USD") setExchangeRate(83.50);
                        else if (type.value === "EUR") setExchangeRate(90.20);
                        else if (type.value === "GBP") setExchangeRate(105.50);
                      }}
                      className={`quick-select-btn ${fromCurrency === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "INR", label: "INR (₹)" },
                    { value: "AED", label: "AED (د.إ)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setFromCurrency(type.value);
                        if (type.value === "INR") setExchangeRate(0.012);
                        else if (type.value === "AED") setExchangeRate(22.75);
                      }}
                      className={`quick-select-btn ${fromCurrency === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* To Currency */}
              <div className="input-group">
                <label>To Currency</label>
                <div className="quick-select-group">
                  {[
                    { value: "INR", label: "INR (₹)" },
                    { value: "USD", label: "USD ($)" },
                    { value: "EUR", label: "EUR (€)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setToCurrency(type.value)}
                      className={`quick-select-btn ${toCurrency === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "GBP", label: "GBP (£)" },
                    { value: "AED", label: "AED (د.إ)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setToCurrency(type.value)}
                      className={`quick-select-btn ${toCurrency === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Exchange Rate */}
              <div className="input-group">
                <label>Exchange Rate (1 {fromCurrency} = ? {toCurrency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">⇄</span>
                  <input
                    type="number"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    className="calc-input"
                    placeholder="83.50"
                    step="0.01"
                  />
                </div>
                <div className="quick-select-group">
                  {[75, 83.50, 90, 100].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setExchangeRate(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conversion Type */}
              <div className="input-group">
                <label>Conversion Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "standard", label: "Standard" },
                    { value: "travel", label: "Travel" },
                    { value: "business", label: "Business" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setConversionType(type.value);
                        if (type.value === "travel") setFeePercentage(3);
                        else if (type.value === "business") setFeePercentage(1);
                        else if (type.value === "standard") setFeePercentage(0);
                      }}
                      className={`quick-select-btn ${conversionType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "remittance", label: "Remittance" },
                    { value: "custom", label: "Custom" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setConversionType(type.value);
                        if (type.value === "remittance") setFeePercentage(2);
                      }}
                      className={`quick-select-btn ${conversionType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fee Percentage */}
              <div className="input-group">
                <label>Conversion Fee (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={feePercentage}
                    onChange={(e) => setFeePercentage(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                    step="0.5"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 1, 2, 3].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setFeePercentage(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Round Up */}
              <div className="input-group">
                <label>Round Amount?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Round Up)" },
                    { value: false, label: "No (Exact)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setRoundUp(type.value)}
                      className={`quick-select-btn ${roundUp === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={convertCurrency} className="calculate-btn">
                Convert Currency
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Conversion Results
              </h2>

              <div className="result-card">
                {/* Total Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Amount ({toCurrency})</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {toCurrency === "INR" ? "₹" : toCurrency === "USD" ? "$" : toCurrency === "EUR" ? "€" : toCurrency === "GBP" ? "£" : ""}{Number(result.totalAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.conversionTypeText} - Including fees
                  </div>
                </div>

                {/* Converted Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Converted Amount</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {toCurrency === "INR" ? "₹" : toCurrency === "USD" ? "$" : toCurrency === "EUR" ? "€" : toCurrency === "GBP" ? "£" : ""}{Number(result.convertedAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At rate {result.exchangeRate}
                  </div>
                </div>

                {/* Original Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Original Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {fromCurrency === "INR" ? "₹" : fromCurrency === "USD" ? "$" : fromCurrency === "EUR" ? "€" : fromCurrency === "GBP" ? "£" : ""}{Number(result.originalAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {fromCurrency} amount
                  </div>
                </div>

                {/* Conversion Fee */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Conversion Fee</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {toCurrency === "INR" ? "₹" : toCurrency === "USD" ? "$" : toCurrency === "EUR" ? "€" : toCurrency === "GBP" ? "£" : ""}{Number(result.conversionFee).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {feePercentage}% service fee
                  </div>
                </div>

                {/* Exchange Rate */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Exchange Rate</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="17 1 21 5 17 9"></polyline>
                        <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
                        <polyline points="7 23 3 19 7 15"></polyline>
                        <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.perUnitRate}
                  </div>
                  <div className="result-subtext">
                    1 {fromCurrency} = {result.perUnitRate} {toCurrency}
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Conversion Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Converted Amount</span>
                      <span className="breakdown-percentage">
                        {toCurrency === "INR" ? "₹" : toCurrency === "USD" ? "$" : toCurrency === "EUR" ? "€" : toCurrency === "GBP" ? "£" : ""}{Number(result.convertedAmount).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseFloat(result.totalAmount) > 0 ? (parseFloat(result.convertedAmount) / parseFloat(result.totalAmount)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Service Fee ({feePercentage}%)</span>
                      <span className="breakdown-percentage">
                        {toCurrency === "INR" ? "₹" : toCurrency === "USD" ? "$" : toCurrency === "EUR" ? "€" : toCurrency === "GBP" ? "£" : ""}{Number(result.conversionFee).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.totalAmount) > 0 ? (parseFloat(result.conversionFee) / parseFloat(result.totalAmount)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Reverse Conversion */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Reverse Conversion</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">1 {toCurrency} equals</span>
                      <span className="breakdown-percentage">
                        {result.reverseRate} {fromCurrency}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">1 {fromCurrency} equals</span>
                      <span className="breakdown-percentage">
                        {result.perUnitRate} {toCurrency}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Currency Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Currency Tips</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.currencyTip} Check rates from multiple sources before large transactions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Currency Conversion</h3>
              <p>
                Currency conversion is the process of exchanging one currency for another at a specific exchange rate. 
                Exchange rates fluctuate constantly based on market demand, economic indicators, interest rates, political 
                stability, and global events. The rate you see (mid-market rate) is usually the average between buying 
                and selling rates on the forex market. However, when you actually exchange money, you'll get a slightly 
                different rate as banks and exchange services add a markup (spread) to make profit. This markup typically 
                ranges from 1-5% depending on the service. Airport currency exchanges often have the worst rates with 
                3-7% markups. Banks usually offer better rates than currency exchange kiosks, but online services like 
                Wise (formerly TransferWise) often have the best rates closest to mid-market rates. For international 
                travel, forex cards or traveler's cards lock in rates and avoid repeated conversion fees. When converting 
                currencies for business or large amounts, consider forward contracts to lock rates for future dates, 
                protecting against unfavorable rate movements. Some services charge flat fees plus percentage markups. 
                Credit cards typically charge 2-3% foreign transaction fees. ATM withdrawals abroad involve bank fees plus 
                currency conversion charges. Understanding these costs helps you choose the most economical method for your needs.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Currency Conversion Formula:</h4>
            <div className="formula-display">
              Converted Amount = Original Amount × Exchange Rate
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Basic Conversion</div>
                <div className="formula-param-value">Amount × Exchange Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">With Fees</div>
                <div className="formula-param-value">Converted Amount + (Converted Amount × Fee %)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Reverse Rate</div>
                <div className="formula-param-value">1 / Exchange Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Markup Calculation</div>
                <div className="formula-param-value">(Offered Rate - Mid Rate) / Mid Rate × 100</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Why do exchange rates differ between services?</h5>
              <p>
                Each service adds its own markup (spread) to the mid-market rate to make profit. Banks typically charge 2-4%, money changers 3-5%, and airports 5-7%. Online services like Wise have lower markups of 0.5-1%.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the best way to exchange currency for travel?</h5>
              <p>
                For travel, forex/travel cards offer good rates and security. Avoid airport exchanges (expensive). Order currency from your bank before travel, or use ATMs abroad (check fees). Credit cards work but charge 2-3% foreign transaction fees.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate the real cost of currency conversion?</h5>
              <p>
                Compare the rate offered to the mid-market rate (check Google/XE.com). Calculate: [(Mid Rate - Offered Rate) / Mid Rate] × 100. Add any flat fees. This gives you the total cost percentage. A 2-3% total cost is reasonable.
              </p>
            </div>

            <div className="faq-item">
              <h5>When is the best time to exchange currency?</h5>
              <p>
                Exchange rates fluctuate constantly. For large amounts, monitor rates over weeks and exchange when favorable. For travel, exchange 1-2 weeks before departure. Avoid last-minute airport exchanges. Consider forward contracts for business needs.
              </p>
            </div>

            <div className="faq-item">
              <h5>What affects exchange rates?</h5>
              <p>
                Exchange rates are influenced by: interest rate differentials, inflation rates, political stability, economic performance (GDP growth), trade balances, government debt, speculation
                </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrencyConverter;